'use client';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import { OptionalPortal, Transition, FocusTrap, Box, closeOnEscape } from '@s7/base';
import { useFocusReturn, useMergedRefs } from '@s7/hooks';
import { useProps, rem } from '@s7/styles';
import { isNumber } from '@s7/utils';
import { usePopoverContext } from '../Popover.context.js';
import { IDENT_POPOVER_DROPDOWN, dropdownDefaultProps } from './PopoverDropdown.contract.js';
import useStyles from './PopoverDropdown.styles.js';
import { FloatingArrow } from '../../Floating/FloatingArrow/FloatingArrow.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var PopoverDropdown = /*#__PURE__*/ forwardRef(function(props, ref) {
    var _useProps = useProps(IDENT_POPOVER_DROPDOWN, dropdownDefaultProps, props), _useProps_ = _useProps[0], style = _useProps_.style, className = _useProps_.className, children = _useProps_.children, onKeyDownCapture = _useProps_.onKeyDownCapture, sxs = _useProps[1], others = _object_without_properties_loose(_useProps[0], [
        "style",
        "className",
        "children",
        "onKeyDownCapture"
    ]);
    var _usePopoverContext = usePopoverContext(), opened = _usePopoverContext.opened, disabled = _usePopoverContext.disabled, withRoles = _usePopoverContext.withRoles, getDropdownId = _usePopoverContext.getDropdownId, getTargetId = _usePopoverContext.getTargetId, radius = _usePopoverContext.radius, shadow = _usePopoverContext.shadow, width = _usePopoverContext.width, zIndex = _usePopoverContext.zIndex, x = _usePopoverContext.x, y = _usePopoverContext.y, floating = _usePopoverContext.floating, trapFocus = _usePopoverContext.trapFocus, placement = _usePopoverContext.placement, keepMounted = _usePopoverContext.keepMounted, onClose = _usePopoverContext.onClose, transitionProps = _usePopoverContext.transitionProps, portalProps = _usePopoverContext.portalProps, withinPortal = _usePopoverContext.withinPortal, arrowX = _usePopoverContext.arrowX, arrowY = _usePopoverContext.arrowY, arrowPosition = _usePopoverContext.arrowPosition, arrowOffset = _usePopoverContext.arrowOffset, arrowRadius = _usePopoverContext.arrowRadius, arrowSize = _usePopoverContext.arrowSize, arrowRef = _usePopoverContext.arrowRef, withArrow = _usePopoverContext.withArrow, context = _object_without_properties_loose(_usePopoverContext, [
        "opened",
        "disabled",
        "withRoles",
        "getDropdownId",
        "getTargetId",
        "radius",
        "shadow",
        "width",
        "zIndex",
        "x",
        "y",
        "floating",
        "trapFocus",
        "placement",
        "keepMounted",
        "onClose",
        "transitionProps",
        "portalProps",
        "withinPortal",
        "arrowX",
        "arrowY",
        "arrowPosition",
        "arrowOffset",
        "arrowRadius",
        "arrowSize",
        "arrowRef",
        "withArrow"
    ]);
    var _useStyles = useStyles({
        radius: radius,
        shadow: shadow
    }, sxs, IDENT_POPOVER_DROPDOWN), classes = _useStyles[0], cx = _useStyles[1];
    var returnFocus = useFocusReturn({
        opened: opened,
        shouldReturnFocus: context.returnFocus
    });
    var mergedRef = useMergedRefs(ref, floating);
    if (disabled) {
        return null;
    }
    var accessibleProps = withRoles ? {
        "aria-labelledby": getTargetId(),
        id: getDropdownId(),
        role: "dialog"
    } : {};
    var boxWidth = width === "target" ? undefined : rem(width);
    var boxStyles = {
        zIndex: zIndex,
        top: y != null ? y : 0,
        left: x != null ? x : 0,
        width: boxWidth
    };
    var transitionExitDuration = isNumber(transitionProps == null ? void 0 : transitionProps.exitDuration) ? transitionProps == null ? void 0 : transitionProps.exitDuration : transitionProps == null ? void 0 : transitionProps.duration;
    var _transitionProps_duration;
    return /*#__PURE__*/ jsx(OptionalPortal, _extends({}, portalProps, {
        withinPortal: withinPortal,
        children: /*#__PURE__*/ jsx(Transition, _extends({
            mounted: opened
        }, transitionProps, {
            duration: (_transitionProps_duration = transitionProps == null ? void 0 : transitionProps.duration) != null ? _transitionProps_duration : 150,
            exitDuration: transitionExitDuration,
            keepMounted: keepMounted,
            transition: (transitionProps == null ? void 0 : transitionProps.transition) || "fade",
            children: function(transitionStyles) {
                return /*#__PURE__*/ jsx(FocusTrap, {
                    active: trapFocus,
                    children: /*#__PURE__*/ jsxs(Box, _extends({}, accessibleProps, {
                        ref: mergedRef,
                        style: _extends({}, style, transitionStyles, boxStyles),
                        className: cx(classes.dropdown, className),
                        "data-position": placement,
                        onKeyDownCapture: closeOnEscape(onClose, {
                            active: context.closeOnEscape,
                            onTrigger: returnFocus,
                            onKeyDown: onKeyDownCapture
                        })
                    }, others, {
                        tabIndex: -1,
                        children: [
                            children,
                            /*#__PURE__*/ jsx(FloatingArrow, {
                                ref: arrowRef,
                                arrowOffset: arrowOffset,
                                arrowPosition: arrowPosition,
                                arrowRadius: arrowRadius,
                                arrowSize: arrowSize,
                                arrowX: arrowX,
                                arrowY: arrowY,
                                className: classes.arrow,
                                position: placement,
                                visible: withArrow
                            })
                        ]
                    }))
                });
            }
        }))
    }));
});
PopoverDropdown.displayName = "Popover.Dropdown";

export { PopoverDropdown };
